"use client";

import type React from "react";
import { useRef, useState } from "react";
import { Flag, Paperclip, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

type ReportType = "VIDEO" | "FORUM_POST" | "COMMENT" | "USER" | "MESSAGE" | "STUDY_GROUP_RESOURCE";

type ReportReason =
  | "SPAM"
  | "HARASSMENT"
  | "HATE_SPEECH"
  | "INAPPROPRIATE_CONTENT"
  | "MISINFORMATION"
  | "COPYRIGHT_VIOLATION"
  | "IMPERSONATION"
  | "PRIVACY_VIOLATION"
  | "OTHER";

const REPORT_REASONS: Record<ReportReason, string> = {
  SPAM: "Spam",
  HARASSMENT: "Harassment",
  HATE_SPEECH: "Hate Speech",
  INAPPROPRIATE_CONTENT: "Inappropriate Content",
  MISINFORMATION: "Misinformation",
  COPYRIGHT_VIOLATION: "Copyright Violation",
  IMPERSONATION: "Impersonation",
  PRIVACY_VIOLATION: "Privacy Violation",
  OTHER: "Other",
};

interface ReportContentDialogProps {
  targetId: string;
  targetType: ReportType;
  /** Author of the reported content, used for optional "also block" . */
  authorId?: string;
  trigger?: React.ReactNode;
  onReportSubmitted?: () => void;
  /**
   * Optional controlled-open state. Use this when the dialog needs to be
   * triggered from somewhere that can't safely host a DialogTrigger (e.g.
   * a DropdownMenuItem — see the known Radix issue where DropdownMenu's
   * lingering `pointer-events: none` on body interferes with the Dialog
   * and leaves the dropdown open behind the dialog).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ReportContentDialog({
  targetId,
  targetType,
  authorId,
  trigger,
  onReportSubmitted,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ReportContentDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    controlledOnOpenChange?.(next);
  };
  const [reason, setReason] = useState<ReportReason | "">("");
  const [details, setDetails] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const resetForm = () => {
    setReason("");
    setDetails("");
    setEvidenceUrl("");
    setAttachmentUrl("");
    setAttachmentName("");
    setError("");
    setAlsoBlock(false);
  };

  const blockUserId = targetType === "USER" ? targetId : authorId;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Attachment must be 10MB or smaller.",
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        let errMsg = "Failed to upload attachment.";
        try {
          const parsed = JSON.parse(errText);
          if (parsed?.error) errMsg = parsed.error;
        } catch {
          /* ignore */
        }
        toast({ title: "Upload failed", description: errMsg, variant: "destructive" });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      const data = await res.json();
      setAttachmentUrl(data.url);
      setAttachmentName(file.name);
    } catch {
      toast({
        title: "Upload failed",
        description: "Please try again.",
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setIsUploading(false);
    }
  };

  const clearAttachment = () => {
    setAttachmentUrl("");
    setAttachmentName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!reason) {
      setError("Please select a reason for reporting.");
      return;
    }

    // Light client-side validation for the link.
    const trimmedLink = evidenceUrl.trim();
    if (trimmedLink) {
      try {
        const u = new URL(trimmedLink);
        if (u.protocol !== "http:" && u.protocol !== "https:") {
          throw new Error("invalid protocol");
        }
      } catch {
        setError("Evidence link must be a valid http(s) URL.");
        return;
      }
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch("/api/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: targetType,
          reason,
          details,
          targetId,
          targetType,
          evidenceUrl: trimmedLink || undefined,
          attachmentUrl: attachmentUrl || undefined,
          attachmentName: attachmentName || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        if (alsoBlock && blockUserId) {
          try {
            await fetch("/api/blocks", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ userId: blockUserId }),
            });
          } catch {
            /* report already succeeded */
          }
        }
        onReportSubmitted?.();
        setOpen(false);
        resetForm();
        toast({ title: "Report submitted", description: "Thank you. Our team will review it shortly." });
      } else {
        toast({ title: "Could not submit report", description: data.error || "Please try again.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Could not submit report", description: "Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="ghost" size="sm">
              <Flag className="h-4 w-4 mr-2" />
              Report
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent
        className="sm:max-w-[425px]"
        // React events bubble through the component tree even when
        // rendered in a portal — stop here so clicks inside the dialog
        // (Submit, Cancel, etc.) never reach an ancestor card's onClick.
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Report Content</DialogTitle>
          <DialogDescription>
            Help keep our community safe by reporting content that violates our guidelines.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Select
              value={reason}
              onValueChange={(value) => setReason(value as ReportReason)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select reason for reporting" />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(REPORT_REASONS) as [ReportReason, string][]).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Textarea
              placeholder="Provide additional details about your report..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="report-evidence-link" className="text-xs text-muted-foreground">
              Evidence link (optional)
            </Label>
            <Input
              id="report-evidence-link"
              type="url"
              inputMode="url"
              placeholder="https://example.com/screenshot-or-source"
              value={evidenceUrl}
              onChange={(e) => setEvidenceUrl(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-xs text-muted-foreground">
              Attachment (optional)
            </Label>
            {attachmentUrl ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Paperclip className="h-4 w-4 shrink-0 text-gray-500" />
                  <span className="truncate text-gray-700">{attachmentName || "Attachment"}</span>
                </div>
                <button
                  type="button"
                  onClick={clearAttachment}
                  className="shrink-0 text-gray-400 hover:text-red-600"
                  aria-label="Remove attachment"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="gap-1.5"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                    </>
                  ) : (
                    <>
                      <Paperclip className="h-4 w-4" /> Add file
                    </>
                  )}
                </Button>
                <span className="text-xs text-muted-foreground">
                  PNG, JPG, PDF, DOC — up to 10MB
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,application/pdf,.doc,.docx,.txt"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
            )}
          </div>
        </div>

        {blockUserId ? (
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <Checkbox
              checked={alsoBlock}
              onCheckedChange={(value) => setAlsoBlock(value === true)}
              className="mt-0.5"
            />
            <span>Also block this user. You will not see their posts or videos, and they cannot message you.</span>
          </label>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              resetForm();
            }}
            disabled={isSubmitting || isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isUploading}
          >
            {isSubmitting ? "Submitting..." : "Submit Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
